export const ANALYSIS_TIMEOUTS = {
  office: 15_000,
  graph: 15_000,
  iframe: 10_000,
  image: 5_000,
  decode: 10_000,
  run: 120_000
};

export class AnalysisError extends Error {
  constructor(public readonly code: 'timeout' | 'cancelled' | 'unavailable' | 'failed', message: string) {
    super(message);
    this.name = 'AnalysisError';
  }
}

export interface AsyncContext {
  signal: AbortSignal;
  deadline: number;
  assertActive(): void;
}

/** Bounds even APIs that cannot be cancelled. Late settlements are consumed, never published. */
export function bounded<T>(work: () => PromiseLike<T> | T, milliseconds: number,
                           operation: string, context?: AsyncContext): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let done = false;
    let timer: ReturnType<typeof setTimeout>;
    const finish = (error?: unknown, value?: T) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      context?.signal.removeEventListener('abort', cancel);
      if (error !== undefined) reject(error);
      else resolve(value as T);
    };
    const cancel = () => finish(new AnalysisError('cancelled', `${operation} cancelled`));
    try {
      context?.assertActive();
      if (context?.signal.aborted) return cancel();
      context?.signal.addEventListener('abort', cancel, { once: true });
      const duration = Math.min(milliseconds, context ? context.deadline - Date.now() : milliseconds);
      timer = setTimeout(() => finish(new AnalysisError('timeout', `${operation} timed out`)), Math.max(0, duration));
      Promise.resolve(work()).then(value => {
        try {
          context?.assertActive();
          finish(undefined, value);
        } catch (error) { finish(error); }
      }, error => finish(error ?? new AnalysisError('failed', `${operation} failed`)));
    } catch (error) { finish(error); }
  });
}

export function officeCall<T>(invoke: (callback: (result: Office.AsyncResult<T>) => void) => void,
                              operation: string, context?: AsyncContext): Promise<T> {
  return bounded(() => new Promise<T>((resolve, reject) => {
    invoke(result => {
      if (result.status === Office.AsyncResultStatus.Succeeded) resolve(result.value);
      else reject(new AnalysisError('failed', `${operation}: ${result.error?.message || 'Office request failed'}`));
    });
  }), ANALYSIS_TIMEOUTS.office, operation, context);
}

/** Adapter for legacy resolve/reject callbacks containing asynchronous work. */
export function asyncCallback<T>(executor: (resolve: (value: T | PromiseLike<T>) => void,
                                           reject: (error?: any) => void) => Promise<unknown>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    Promise.resolve().then(() => executor(resolve, reject)).catch(reject);
  });
}
